<template>
  <WidgetCadre
    titre="Postes en défaut"
    v-bind="apparence"
    :lien="donnees?.lien || null"
    :chargement="chargement"
    :panne="panne"
    :reglable="reglable"
    :etat="etat"
    @retirer="$emit('retirer')"
    @recharger="$emit('recharger')"
  >
    <div class="postes">
      <!-- La jauge montre la part du parc en ligne : c'est elle qui dit si
           quarante postes en défaut sont un incident ou un bruit de fond. -->
      <svg
        class="jauge"
        viewBox="0 0 120 120"
        role="img"
        :aria-label="`${pourcentage ?? '—'} % des postes en ligne`"
      >
        <circle
          class="jauge__piste"
          cx="60"
          cy="60"
          :r="RAYON"
        />
        <circle
          v-if="pourcentage !== null"
          class="jauge__arc"
          cx="60"
          cy="60"
          :r="RAYON"
          :stroke-dasharray="`${arc} ${CIRCONFERENCE}`"
        />
        <text
          class="jauge__valeur"
          x="60"
          y="60"
        >{{ pourcentage ?? '—' }}<tspan class="jauge__unite">%</tspan></text>
        <text
          class="jauge__legende"
          x="60"
          y="80"
        >en ligne</text>
      </svg>

      <dl class="postes__detail">
        <div>
          <dd class="chiffre chiffre--grand">
            {{ donnees?.en_defaut ?? '—' }}
          </dd>
          <dt class="etiquette">
            en défaut sur {{ donnees?.total ?? '—' }}
          </dt>
        </div>
        <div class="postes__ligne">
          <dd class="chiffre chiffre--moyen">
            {{ donnees?.deconnectes ?? '—' }}
          </dd>
          <dt class="etiquette">
            déconnectés
          </dt>
        </div>
        <div class="postes__ligne">
          <dd class="chiffre chiffre--moyen">
            {{ donnees?.lagues ?? '—' }}
          </dd>
          <dt class="etiquette">
            en latence
          </dt>
        </div>
      </dl>
    </div>
  </WidgetCadre>
</template>

<script setup>
import { computed } from 'vue'
import WidgetCadre from './WidgetCadre.vue'
import { apparenceDe } from './apparence'

const props = defineProps({
    donnees: { type: Object, default: null },
    chargement: { type: Boolean, default: false },
    panne: { type: Boolean, default: false },
    reglable: { type: Boolean, default: false },
})

defineEmits(['retirer', 'recharger'])

const apparence = apparenceDe('postes-en-defaut')

const RAYON = 48
const CIRCONFERENCE = 2 * Math.PI * RAYON

/**
 * Arrondi vers le bas : 99,6 % affiché « 100 % » annoncerait un parc sans
 * défaut alors que le compteur d'à côté en montre.
 */
const pourcentage = computed(() => {
    const total = props.donnees?.total

    if (!total) return null

    return Math.floor(((total - (props.donnees.en_defaut ?? 0)) / total) * 100)
})

const arc = computed(() => ((pourcentage.value ?? 0) / 100) * CIRCONFERENCE)

const etat = computed(() => {
    if (!props.donnees) return 'repos'

    return props.donnees.en_defaut > 0 ? 'attention' : 'sain'
})
</script>

<style lang="scss" scoped>
.postes {
  display: flex;
  align-items: center;
  gap: 16px;
  height: 100%;

  &__detail {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: 0;

    dt, dd { margin: 0; }
  }

  &__ligne {
    display: flex;
    align-items: baseline;
    gap: 8px;
  }
}

.jauge {
  width: 112px;
  height: 112px;
  flex-shrink: 0;

  &__piste,
  &__arc {
    fill: none;
    stroke-width: 11;
  }

  &__piste { stroke: color-mix(in srgb, var(--accent) 18%, transparent); }

  &__arc {
    stroke: var(--accent);
    stroke-linecap: round;
    transform: rotate(-90deg);
    transform-origin: 60px 60px;
  }

  &__valeur {
    fill: var(--encre);
    font-size: 26px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    text-anchor: middle;
    dominant-baseline: middle;
  }

  &__unite { font-size: 14px; }

  &__legende {
    fill: var(--el-text-color-secondary);
    font-size: 11px;
    text-anchor: middle;
  }
}
</style>
